import type { LevelConfig, LevelLayer, LevelPiece, LevelPieceType } from "../level_config.js";

// Same numeric value as PlayDialogDifficulty.Common — kept as a literal
// instead of importing play_dialog.ts, same leaf-module reasoning as the
// other showcases' local COMMON_DIFFICULTY.
const COMMON_DIFFICULTY = 0;

/**
 * Four 1x3 LOGS (LevelPieceType.Tree) stood into a teepee on a stone platform:
 * one leaning in from the left, one from the right, one from behind and one
 * from the front.
 *
 * The left and right logs share a z of 0; the back and front share an x of 0.
 * That is the whole shape of the problem, because four bars a cube thick cannot
 * all reach the same point — held on their own axes they would spawn inside one
 * another, and the solver answers a spawn overlap with a shove. So the ring is
 * built in two tiers instead of one:
 *
 *   - LEFT and RIGHT lean towards each other in the screen plane
 *     (rotationDegZ) and meet: an A-frame, self-supporting, its two top end
 *     faces touching along the axis. Its bars are a cube deep, so near the top
 *     the pair fills z -0.5..+0.5;
 *   - BACK and FRONT lean into depth (rotationDegX, positive tips a top towards
 *     the camera) and stop against those flanks rather than against each other.
 *     Their feet therefore stand further out — FLANK_RADIUS instead of
 *     APEX_RADIUS.
 *
 * Each of the outer two lands astride the notch between the A-frame's tops, so
 * it rests on both at once, and the pair pushes the A-frame from opposite sides
 * with forces that cancel. Every log's inward topple is blocked, and nothing
 * has to pass through anything.
 *
 * The platform underneath is stone rather than anything else on purpose. A
 * leaning pair pushes its feet out with H = (W / 2) * tan(theta) against a
 * normal load of W, so the feet need mu >= tan(theta) / 2 — 0.42 at 40 degrees.
 * On the bare tabletop that was TreeBlock.FRICTION 0.45 averaged with Rapier's
 * default 0.5 (PhysicsTable never calls setFriction), about 0.475, a margin of
 * 13 per cent. Stone is 0.75, which brings the pair to 0.60 and the margin to
 * 43.
 */

// Plan of the platform, in cells. Both cover the logs' feet, which reach 2.72
// cubes out in x and 3.22 in z: seven cells span 3.57 each way, five would stop
// at 2.55 and leave the back and front logs standing on air.
const COLS = 7;
const LAYERS = 7;
// Height of the platform, in rows.
const WALL_ROWS = 5;

const BAR_HEIGHT = 3;
const ROWS = WALL_ROWS + BAR_HEIGHT;

// Column and layer the teepee is centred on.
const AXIS = ( COLS - 1 ) / 2;
const MID_LAYER = ( LAYERS - 1 ) / 2;

// Pitch between grid columns and between depth layers, as a multiple of a cube
// — mirrors PhysicsCube.GRID_GAP_RATIO. Rows have no such gap, so the vertical
// maths below is in plain cubes while offsetX and offsetZ have to be divided.
const CELL_PITCH = 1.02;
// Half a log's thickness, in cubes.
const HALF_THICK = 0.5;

/** Stone, for the platform. The logs are Tree. */
const WALL_TYPE = 2;
const LOG_TYPE = 7;

/**
 * Lean from vertical, degrees.
 *
 * The pad wants 40..50: outside that window edge_support_pad leaves a log
 * standing on a bare corner. Friction wants the other direction — see the
 * platform note above — so 40 is the value with margin at both ends.
 */
const LEAN_DEG = 40;

/** Breathing room so a contact starts as a touch rather than an overlap. */
const SLACK = 0.03;

/**
 * Where the A-frame's two logs stand so their top ends meet without starting
 * inside each other. Put the lower corner of one top end exactly on the
 * mirrored log's side face and the cosines collapse to this — 1.347 cubes at 40
 * degrees.
 */
function apexRadius( deg: number ): number
{
  const rad = deg * Math.PI / 180;

  return ( BAR_HEIGHT * Math.sin( rad ) + Math.cos( rad ) ) / 2;
}

/**
 * Where the other two stand so their top ends stop on the A-frame's flank at
 * z = +/-0.5 rather than driving through it. Same corner, measured to a flat
 * face instead of to a mirrored bar — 1.847 cubes at 40 degrees, half a cube
 * further out than the pair it leans on.
 */
function flankRadius( deg: number ): number
{
  const rad = deg * Math.PI / 180;

  return HALF_THICK + ( BAR_HEIGHT / 2 ) * Math.sin( rad ) + HALF_THICK * Math.cos( rad );
}

/**
 * Drop needed so a tipped log's lowest corner lands on the platform rather than
 * hovering. Upright it is placed with its base on the deck; tipped, its
 * half-height shrinks from BAR_HEIGHT / 2 to this, and the difference is the
 * gap that would otherwise be there.
 */
function tippedHalfHeight( deg: number ): number
{
  const rad = deg * Math.PI / 180;

  return ( BAR_HEIGHT / 2 ) * Math.cos( rad ) + HALF_THICK * Math.sin( rad );
}

interface Log
{
  /** Depth layer it is authored into, nearest to where it actually stands. */
  layer: number;
  /** Grid column its cells are claimed in — bookkeeping only, see makeLayers. */
  column: number;
  centre_x: number;
  centre_z: number;
  rotationDegX?: number;
  rotationDegZ?: number;
}

function teepee(): Log[]
{
  const apex = apexRadius( LEAN_DEG ) + SLACK;
  const flank = flankRadius( LEAN_DEG ) + SLACK;
  // Depth is carried by a real layer plus a remainder, not by offsetZ alone:
  // field_layout adds the spread of offsetZ straight onto the layer count when
  // it sizes the tabletop, so a log parked two layers away purely on offset
  // would stretch the top by two layers it does not need.
  const flank_layer = Math.round( flank / CELL_PITCH );

  // A positive rotationDegZ carries a top towards -x, a positive rotationDegX
  // carries it towards +z. Every log leans back towards the middle.
  return [
    { layer: MID_LAYER, column: 2, centre_x: -apex, centre_z: 0, rotationDegZ: -LEAN_DEG },
    { layer: MID_LAYER, column: 4, centre_x: apex, centre_z: 0, rotationDegZ: LEAN_DEG },
    { layer: MID_LAYER - flank_layer, column: 3, centre_x: 0, centre_z: -flank, rotationDegX: LEAN_DEG },
    { layer: MID_LAYER + flank_layer, column: 3, centre_x: 0, centre_z: flank, rotationDegX: -LEAN_DEG },
  ];
}

function makeLayers(): LevelLayer[]
{
  const logs = teepee();
  const drop = tippedHalfHeight( LEAN_DEG ) - BAR_HEIGHT / 2;

  return Array.from( { length: LAYERS }, ( _unused, layer ) =>
  {
    const grid = Array.from( { length: ROWS }, () => Array( COLS ).fill( 0 ) );
    const pieces: LevelPiece[] = [];
    let id = 1;

    // The platform: solid stone across the whole plan, WALL_ROWS deep, in every
    // layer. Row 0 is the TOP of the wall, so the platform occupies the bottom
    // rows and the logs get the BAR_HEIGHT above it.
    for ( let row = BAR_HEIGHT; row < ROWS; row++ )
      for ( let col = 0; col < COLS; col++ )
      {
        grid[row][col] = id;
        pieces.push( {
          id,
          type: WALL_TYPE as LevelPieceType,
          x: col,
          y: row,
          width: 1,
          height: 1,
        } );
        id++;
      }

    for ( const log of logs )
    {
      if ( log.layer !== layer )
        continue;

      // The claimed column is bookkeeping for the splash-damage neighbour
      // lookup, which folds offsetX back in, so a log lands at the same place
      // whichever column it claims. It only has to be a column no other piece
      // in these rows has taken.
      for ( let row = 0; row < BAR_HEIGHT; row++ )
        grid[row][log.column] = id;

      pieces.push( {
        id,
        type: LOG_TYPE as LevelPieceType,
        x: log.column,
        y: 0,
        width: 1,
        height: BAR_HEIGHT,
        offsetX: AXIS + log.centre_x / CELL_PITCH - log.column,
        offsetY: drop,
        offsetZ: log.centre_z / CELL_PITCH - ( log.layer - MID_LAYER ),
        rotationDegX: log.rotationDegX,
        rotationDegZ: log.rotationDegZ,
      } );

      id++;
    }

    return { grid, pieces };
  } );
}

const LEANING_LOGS_LEVEL: LevelConfig = {
  level: 90309,
  difficulty: COMMON_DIFFICULTY as LevelConfig["difficulty"],
  tables: [
    { layers: makeLayers(), slot: { u: 0, v: 0 } },
  ],
};

export class LeaningLogsShowcase
{
  public static readonly LEVEL = LEANING_LOGS_LEVEL.level;

  private static active = false;

  public static isActive(): boolean
  {
    return this.active;
  }

  public static activate(): number
  {
    this.active = true;
    return this.LEVEL;
  }

  public static deactivate(): void
  {
    this.active = false;
  }

  public static getConfig( level: number ): LevelConfig | null
  {
    return level === this.LEVEL ? LEANING_LOGS_LEVEL : null;
  }
}
